import React, { useState, useRef, useEffect } from "react";

/**
 * A searchable dropdown that replaces a native <select> when the option
 * list is long enough that typing to filter is helpful.
 *
 * Props:
 *   options      – [{ id, name }]  (or any objects with a `name` property)
 *   value        – currently selected value (string, matched against option.name)
 *   onChange     – (value) => void
 *   placeholder  – text shown when nothing is selected
 *   disabled     – boolean
 *   required     – boolean
 *   className    – tailwind classes for the trigger button
 *   variant      – "light" (dark text on white, for HeroBanner) | "dark" (light text on dark bg, for TopBanner)
 */
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  required = false,
  className = "",
  variant = "light",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find((o) => o.name === value);

  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.name);
    setIsOpen(false);
    setSearch("");
  };

  const isDark = variant === "dark";

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`${className} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} flex items-center justify-between w-full`}
      >
        <span className={selectedOption ? "" : isDark ? "text-gray-500" : "text-gray-400"}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <svg
          className={`pointer-events-none w-4 h-4 ${isDark ? "text-gray-500" : "text-gray-500"} transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className={`absolute z-50 mt-1 w-full rounded-lg shadow-lg border max-h-[240px] overflow-hidden flex flex-col ${
            isDark
              ? "bg-gray-800 border-gray-600"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Search input */}
          <div className={`p-2 border-b ${isDark ? "border-gray-600" : "border-gray-200"}`}>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full px-3 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-1 ${
                isDark
                  ? "bg-gray-700 border-gray-500 text-white placeholder-gray-400 focus:ring-primary"
                  : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:ring-primary"
              }`}
              autoComplete="off"
            />
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                No results found
              </div>
            ) : (
              filtered.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleSelect(option)}
                  className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                    option.name === value
                      ? isDark
                        ? "bg-primary/20 text-white font-medium"
                        : "bg-primary/10 text-primary font-medium"
                      : isDark
                        ? "text-gray-300 hover:bg-gray-700"
                        : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {option.name}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Hidden native input for form validation */}
      {required && (
        <input
          type="text"
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="absolute opacity-0 pointer-events-none -z-10"
        />
      )}
    </div>
  );
};

export default SearchableSelect;
